import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FormDataInterface, FormStep, RecommandationTravaux, FormErrors, TypeChauffage, TypeComposant } from './types';
import './App.css';
import { calculateDeperditions, calculateConsommation, analyserTravauxPotentiels, estimerPuissanceChauffage } from './services/calculations';
import jddData from './resources/jdd.json';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const App: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<FormStep>('general');
  const [formData, setFormData] = useState<FormDataInterface>({
    general: {
      typeLogement: 'maison',
      usagePrincipalImmeuble: 'habitation',
      anneeConstruction: new Date().getFullYear(),
      surfaceHabitable: 0,
      nombreEtages: 1,
      zoneClimatique: 'H1a',
      copropriete: false,
      numeroImmatriculationCopropriete: '',
      adresse: {
        rue: '',
        codePostal: '',
        ville: '',
        etage: '',
        numeroAppartement: '',
        complementAdresse: '',
        referenceCadastrale: '',
        invariantFiscal: ''
      }
    },
    murs: {
      type: 'beton',
      surface: 0,
      epaisseur: 20,
      isolation: {
        type: ''
      }
    },
    toiture: {
      surface: 0,
      isolation: {
        type: ''
      },
      type: 'comblesPerdus',
      materiaux: 'tuiles'
    },
    plancher: {
      surface: 0,
      type: 'terrePlein',
      materiaux: 'beton',
      isolation: {
        type: ''
      }
    },
    fenetres: {
      type: 'simple',
      surface: 0
    },
    chauffage: {
      type: 'gazRecent',
      emetteurs: 'radiateurs',
      energie: 'gaz',
      dateInstallation: new Date().getFullYear(),
      hasVeilleuse: false,
      rendement: 0
    },
    ecs: {
      type: 'ballonElectrique',
      energie: 'electricite',
      puissance: 0,
      rendement: 0,
      dateFabrication: new Date().getFullYear()
    },
    temperatures: {
      interieure: 19,
      exterieure: 7
    }
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [results, setResults] = useState<{
    deperditions: {
      total: number;
      parComposant: {
        murs: number;
        toiture: number;
        plancher: number;
        fenetres: number;
      };
    };
    consommation: number;
    recommandations: RecommandationTravaux[];
  } | null>(null);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isNewContent, setIsNewContent] = useState(false);
  const [showRecap, setShowRecap] = useState(false);

  const [activeRecommandations, setActiveRecommandations] = useState<boolean[]>([]);

  useEffect(() => {
    if (results?.recommandations) {
      setActiveRecommandations(new Array(results.recommandations.length).fill(false));
    }
  }, [results?.recommandations]);

  const validateStep = useCallback((step: FormStep): boolean => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 'general':
        if (formData.general.surfaceHabitable <= 0) {
          newErrors.general = { surfaceHabitable: 'La surface habitable doit être supérieure à 0' };
        }
        if (formData.general.nombreEtages <= 0) {
          newErrors.general = { ...newErrors.general, nombreEtages: 'Le nombre d\'étages doit être supérieur à 0' };
        }
        break;

      case 'murs':
        if (formData.murs.surface <= 0) {
          newErrors.murs = { surface: 'La surface des murs doit être supérieure à 0' };
        }
        break;

      case 'toiture':
        if (formData.toiture.surface <= 0) {
          newErrors.toiture = { surface: 'La surface de toiture doit être supérieure à 0' };
        }
        break;

      case 'plancher':
        if (formData.plancher.surface <= 0) {
          newErrors.plancher = { surface: 'La surface du plancher doit être supérieure à 0' };
        }
        break;

      case 'fenetres':
        if (formData.fenetres.surface < 0) {
          newErrors.fenetres = { surface: 'La surface des fenêtres ne peut pas être négative' };
        }
        break;

      case 'temperatures':
        if (formData.temperatures.interieure < 16 || formData.temperatures.interieure > 25) {
          newErrors.temperatures = { interieure: 'La température intérieure doit être entre 16°C et 25°C' };
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((step: FormStep, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [field]: value
      }
    }));
  }, []);

  const handleCalculate = useCallback(() => {
    const deperditions = calculateDeperditions(formData);
    const consommation = calculateConsommation(
      deperditions.total,
      formData.temperatures.interieure,
      formData.temperatures.exterieure,
      formData.chauffage.type
    );
    const recommandations = analyserTravauxPotentiels(formData, deperditions, consommation);
  
    setResults({
      deperditions,
      consommation,
      recommandations
    });
  }, [formData]);

  const nextStep = useCallback(() => {
    if (validateStep(currentStep)) {
      const steps: FormStep[] = ['general', 'murs', 'toiture', 'plancher', 'fenetres', 'chauffage', 'ecs', 'temperatures'];
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex < steps.length - 1) {
        setCurrentStep(steps[currentIndex + 1]);
      } else {
        handleCalculate();
      }
    }
  }, [currentStep, validateStep, handleCalculate]);

  const prevStep = useCallback(() => {
    const steps: FormStep[] = ['general', 'murs', 'toiture', 'plancher', 'fenetres', 'chauffage', 'ecs', 'temperatures'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  }, [currentStep]);

  const hasSurface = (component: any): component is { surface: number } => {
    return 'surface' in component;
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(num));
  };

  const getAmortissementClass = (gain: number) => {
    if (gain <= 0) return 'text-red-600 dark:text-red-400';
    if (gain < 5000) return 'text-orange-600 dark:text-orange-400';
    if (gain < 15000) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const loadJDD = (key: keyof typeof jddData) => {
    setIsNewContent(true);
    setIsTransitioning(true);

    setTimeout(() => {
      const dataToImport = jddData[key] as FormDataInterface;
      setFormData(dataToImport);

      const deperditions = calculateDeperditions(dataToImport);
      const consommation = calculateConsommation(
        deperditions.total,
        dataToImport.temperatures.interieure,
        dataToImport.temperatures.exterieure,
        dataToImport.chauffage.type
      );
      const recommandations = analyserTravauxPotentiels(dataToImport, deperditions, consommation);

      setResults({
        deperditions,
        consommation,
        recommandations
      });

      setTimeout(() => {
        setIsTransitioning(false);
        setIsNewContent(false);
      }, 300);
    }, 100);
  };

  const getActiveRecommandationsTotals = useMemo(() => {
    const selectedRecommandations = results?.recommandations.filter((_, index) => 
      activeRecommandations[index] === true
    ) || [];

    return {
      coutTotal: selectedRecommandations.reduce((sum, reco) => sum + reco.coutEstime, 0),
      economiesAnnuelles: selectedRecommandations.reduce((sum, reco) => sum + reco.economieEnergie * 0.174, 0)
    };
  }, [results?.recommandations, activeRecommandations]);

  const costData = useMemo(() => {
    if (!results || getActiveRecommandationsTotals.economiesAnnuelles === 0) return [];
    
    return Array.from({ length: 21 }, (_, i) => ({
      annee: i,
      sansTravaux: results.consommation * 0.174 * i,
      avecTravaux: (results.consommation * 0.174 * i) - 
                   (getActiveRecommandationsTotals.economiesAnnuelles * i) + 
                   getActiveRecommandationsTotals.coutTotal
    }));
  }, [results, getActiveRecommandationsTotals]);

  const generateCostData = (
    consommationInitiale: number,
    coutTravaux: number,
    economiesAnnuelles: number
  ) => {
    console.log('Generating cost data:', { consommationInitiale, coutTravaux, economiesAnnuelles }); // Debug
    
    const data = Array.from({ length: 21 }, (_, i) => ({
      annee: i,
      sansTravaux: consommationInitiale * 0.174 * i,
      avecTravaux: (consommationInitiale * 0.174 * i) - (economiesAnnuelles * i) + coutTravaux
    }));

    console.log('Generated data:', data); // Debug
    return data;
  };

  const toggleRecommandation = (index: number) => {
    setActiveRecommandations(prev => {
      const newActiveRecommandations = [...prev];
      newActiveRecommandations[index] = !newActiveRecommandations[index];
      return newActiveRecommandations;
    });
  };

  // Fonction pour générer la recommandation
  const getFinancialAdvice = (coutTotal: number, economiesAnnuelles: number) => {
    const tempsRetour = coutTotal / economiesAnnuelles;
    const gainSur20Ans = economiesAnnuelles * 20 - coutTotal;
    
    if (tempsRetour <= 5) {
      return {
        type: 'excellent',
        color: 'text-green-600 dark:text-green-400',
        message: "Excellent investissement ! Le temps d'amortissement est très court et les économies à long terme sont significatives. Ces travaux sont fortement recommandés."
      };
    } else if (tempsRetour <= 10) {
      return {
        type: 'bon',
        color: 'text-blue-600 dark:text-blue-400',
        message: "Bon investissement avec un temps d'amortissement raisonnable. Ces travaux méritent d'être envisagés sérieusement."
      };
    } else if (tempsRetour <= 15) {
      return {
        type: 'moyen',
        color: 'text-yellow-600 dark:text-yellow-400',
        message: `Investissement à considérer avec attention. Le temps d'amortissement est long mais les gains sur 20 ans (${formatNumber(gainSur20Ans)}€) restent intéressants.`
      };
    } else {
      return {
        type: 'long',
        color: 'text-orange-600 dark:text-orange-400',
        message: "Le temps d'amortissement est important. Envisagez de prioriser d'autres travaux ou de phaser ces rénovations dans le temps."
      };
    }
  };

  const renderJddButtons = () => {
    const jddCategories = {
      maisons: Object.entries(jddData).filter(([key]) => key.startsWith('maison')),
      appartements: Object.entries(jddData).filter(([key]) => key.startsWith('appartement'))
    };

    return (
      <div className="mb-8 grid grid-cols-2 gap-4">
        {Object.entries(jddCategories).map(([category, items]) => (
          <div key={category} className="space-y-4">
            <h3 className="font-semibold text-gray-700 dark:text-gray-300">
              {category === 'maisons' ? 'Maisons' : 'Appartements'}
            </h3>
            {items.map(([key, _]) => (
              <button
                key={key}
                onClick={() => loadJDD(key as keyof typeof jddData)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 
                  rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                {formatJddName(key)}
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const formatJddName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace('maison', 'Maison')
      .replace('appartement', 'Appartement');
  };

  const formatChauffageType = (type: TypeChauffage): string => {
    const types = {
      'gazRecent': 'Chaudière gaz récente',
      'gazAncien': 'Chaudière gaz ancienne',
      'electrique': 'Chauffage électrique',
      'pac': 'Pompe à chaleur',
      'fioulRecent': 'Chaudière fioul récente',
      'fioulAncien': 'Chaudière fioul ancienne',
      'granules': 'Poêle à granulés',
      'solaire': 'Système solaire'
    };
    return types[type] || type;
  };

  const formatNomComposant = (composant: TypeComposant): string => {
    const noms = {
      murs: 'Murs',
      toiture: 'Toiture',
      plancher: 'Plancher',
      fenetres: 'Fenetres',
      chauffage: 'Chauffage',
      ecs: 'ECS',
      temperatures: 'Températures'
    };
    return noms[composant] || composant;
  };

  const handleRecommandationToggle = (index: number) => {
    setActiveRecommandations(prev => {
      const newActiveRecommandations = [...prev];
      newActiveRecommandations[index] = !newActiveRecommandations[index];
      return newActiveRecommandations;
    });
  };

  return (
    <div className="container">
      <h1 
        onClick={() => {
          setResults(null);
          setCurrentStep('general');
        }}
        className="text-4xl font-bold text-center mb-12 cursor-pointer 
          text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 
          transition-colors duration-200 select-none"
      >
        Simulateur d'audit énergétique
      </h1>
      
      <div className={`
        transition-all duration-300 transform
        ${isTransitioning && isNewContent ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}
        ${isTransitioning && !isNewContent ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}
      `}>
        {!results && renderJddButtons()}

        {!results ? (
          <>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill"
                style={{ width: `${((['general', 'murs', 'toiture', 'plancher', 'fenetres', 'chauffage', 'ecs', 'temperatures'].indexOf(currentStep) + 1) / 8) * 100}%` }}
              />
            </div>

            <div className="form-step">
              {/* Sections du formulaire */}
              {currentStep === 'general' && (
                <div className="form-container">
                  <h2 className="section-title">Caractéristiques générales</h2>
                  <div className="space-y-4">
                    <div className="input-group">
                      <label className="input-label">Type de logement</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                   appearance-none cursor-pointer
                                   bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                   bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.general.typeLogement}
                        onChange={(e) => handleChange('general', 'typeLogement', e.target.value)}
                      >
                        <option value="maison">Maison individuelle</option>
                        <option value="appartement">Appartement</option>
                        <option value="immeuble">Immeuble</option>
                      </select>
                    </div>

                    {formData.general.typeLogement === 'immeuble' && (
                      <div className="input-group">
                        <label className="input-label">Usage principal de l'immeuble</label>
                        <select
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                    appearance-none cursor-pointer
                                    bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                    bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                          value={formData.general.usagePrincipalImmeuble}
                          onChange={(e) => handleChange('general', 'usagePrincipalImmeuble', e.target.value)}
                        >
                          <option value="habitation">Habitation</option>
                          <option value="bureau">Bureau</option>
                          <option value="commerce">Commerce</option>
                        </select>
                      </div>
                    )}

                    <div className="input-group">
                      <label className="input-label">Adresse du logement</label>
                      <input 
                        type="text"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Numéro et nom de rue"
                        value={formData.general.adresse.rue}
                        onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, rue: e.target.value})}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="input-group">
                        <label className="input-label">Code postal</label>
                        <input 
                          type="text"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Code postal"
                          value={formData.general.adresse.codePostal}
                          onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, codePostal: e.target.value})}
                        />
                      </div>
                      <div className="input-group">
                        <label className="input-label">Ville</label>
                        <input 
                          type="text"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ville"
                          value={formData.general.adresse.ville}
                          onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, ville: e.target.value})}
                        />
                      </div>
                    </div>

                    {(formData.general.typeLogement === 'appartement') && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="input-group">
                            <label className="input-label">Étage</label>
                            <input 
                              type="text"
                              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Ex: RDC, 1er, 2ème..."
                              value={formData.general.adresse.etage}
                              onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, etage: e.target.value})}
                            />
                          </div>
                          <div className="input-group">
                            <label className="input-label">N° d'appartement</label>
                            <input 
                              type="text"
                              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Numéro ou identifiant"
                              value={formData.general.adresse.numeroAppartement}
                              onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, numeroAppartement: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="input-group">
                      <label className="input-label">Complément d'adresse (optionnel)</label>
                      <input 
                        type="text"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Bâtiment, résidence, etc."
                        value={formData.general.adresse.complementAdresse}
                        onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, complementAdresse: e.target.value})}
                      />
                    </div>

                    {(formData.general.typeLogement === 'appartement' || formData.general.typeLogement === 'immeuble') && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="input-group">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                checked={formData.general.copropriete || false}
                                onChange={(e) => handleChange('general', 'copropriete', e.target.checked)}
                              />
                              <span className="input-label">En copropriété</span>
                            </label>
                          </div>
                          {formData.general.copropriete && (
                            <div className="input-group">
                              <label className="input-label">Numéro d'immatriculation de la copropriété</label>
                              <input
                                type="text"
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                          focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Ex: AB1234567"
                                value={formData.general.numeroImmatriculationCopropriete || ''}
                                onChange={(e) => handleChange('general', 'numeroImmatriculationCopropriete', e.target.value)}
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Numéro à 7 caractères disponible sur le registre national des copropriétés
                              </p>
                            </div>
                          )}
                          {formData.general.copropriete && formData.general.numeroImmatriculationCopropriete && (
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400 font-bold">N° d'immatriculation</span>
                              <span>{formData.general.numeroImmatriculationCopropriete}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="input-group">
                      <label className="input-label">Référence cadastrale</label>
                      <input 
                        type="text"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ex: AB 123"
                        value={formData.general.adresse.referenceCadastrale}
                        onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, referenceCadastrale: e.target.value})}
                      />
                    </div>

                    {(formData.general.typeLogement === 'maison' || formData.general.typeLogement === 'appartement') && (
                      <div className="input-group">
                        <label className="input-label">Invariant fiscal du logement</label>
                        <input 
                          type="text"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ex: 1234567890"
                          value={formData.general.adresse.invariantFiscal}
                          onChange={(e) => handleChange('general', 'adresse', {...formData.general.adresse, invariantFiscal: e.target.value})}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          L'invariant fiscal est un identifiant unique à 14 chiffres qui figure sur votre avis d'imposition ou taxe foncière
                        </p>
                      </div>
                    )}

                    <div className="input-group">
                      <label className="input-label">Année de construction du bâtiment (ou estimation si inconnue)</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.general?.anneeConstruction ? 'input-error' : ''}`}
                        value={formData.general.anneeConstruction}
                        onChange={(e) => handleChange('general', 'anneeConstruction', Number(e.target.value))}
                      />
                      {errors.general?.anneeConstruction && (
                        <span className="error-message">{errors.general.anneeConstruction}</span>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Surface habitable (m²)</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.general?.surfaceHabitable ? 'input-error' : ''}`}
                        value={formData.general.surfaceHabitable}
                        onChange={(e) => handleChange('general', 'surfaceHabitable', Number(e.target.value))}
                      />
                      {errors.general?.surfaceHabitable && (
                        <span className="error-message">{errors.general.surfaceHabitable}</span>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Nombre d'étages</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.general?.nombreEtages ? 'input-error' : ''}`}
                        value={formData.general.nombreEtages}
                        onChange={(e) => handleChange('general', 'nombreEtages', Number(e.target.value))}
                      />
                      {errors.general?.nombreEtages && (
                        <span className="error-message">{errors.general.nombreEtages}</span>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Zone climatique</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.general.zoneClimatique}
                        onChange={(e) => handleChange('general', 'zoneClimatique', e.target.value)}
                      >
                        {(['H1a', 'H1b', 'H1c', 'H2a', 'H2b', 'H2c', 'H2d', 'H3'] as const).map(zone => (
                          <option key={zone} value={zone}>{zone}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 'murs' && (
                <div className="form-container">
                  <h2 className="section-title">Caractéristiques des murs</h2>
                  <div className="space-y-4">
                    <div className="input-group">
                      <label className="input-label">Type de murs</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.murs.type}
                        onChange={(e) => handleChange('murs', 'type', e.target.value)}
                      >
                        <option value="beton">Béton</option>
                        <option value="brique">Brique</option>
                        <option value="pierres">Pierre</option>
                        <option value="parpaing">Parpaing</option>
                        <option value="boisMassif">Bois massif</option>
                        <option value="ossatureBois">Ossature bois</option>
                        <option value="piseTerre">Pisé/Terre</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Épaisseur des murs (cm)</label>
                      <input 
                        type="number"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.murs.epaisseur || ''}
                        onChange={(e) => handleChange('murs', 'epaisseur', Number(e.target.value))}
                        min="1"
                        max="200"
                        step="1"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Épaisseur moyenne des murs extérieurs, hors isolation
                      </p>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Surface (m²)</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.murs?.surface ? 'input-error' : ''}`}
                        value={formData.murs.surface}
                        onChange={(e) => handleChange('murs', 'surface', Number(e.target.value))}
                      />
                      {errors.murs?.surface && (
                        <span className="error-message">{errors.murs.surface}</span>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Type d'isolation</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.murs.isolation?.type || ''}
                        onChange={(e) => handleChange('murs', 'isolation', { type: e.target.value })}
                      >
                        <option value="">Aucune isolation</option>
                        <option value="laineVerre">Laine de verre</option>
                        <option value="laineRoche">Laine de roche</option>
                        <option value="polystyrene">Polystyrène</option>
                        <option value="polyurethane">Polyuréthane</option>
                        <option value="fibreBois">Fibre de bois</option>
                        <option value="chanvre">Chanvre</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 'toiture' && (
                <div className="form-container">
                  <h2 className="section-title">Caractéristiques de la toiture</h2>
                  <div className="space-y-4">
                    <div className="input-group">
                      <label className="input-label">Type de toiture</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.toiture.type}
                        onChange={(e) => handleChange('toiture', 'type', e.target.value)}
                      >
                        <option value="comblesPerdus">Combles perdus</option>
                        <option value="comblesAmenages">Combles aménagés</option>
                        <option value="terrasse">Toiture-terrasse</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Matériau de couverture</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.toiture.materiaux}
                        onChange={(e) => handleChange('toiture', 'materiaux', e.target.value)}
                      >
                        <option value="tuiles">Tuiles</option>
                        <option value="ardoises">Ardoises</option>
                        <option value="zinc">Zinc</option>
                        <option value="bac">Bac acier</option>
                        <option value="beton">Béton</option>
                        <option value="shingle">Shingle/Bardeau</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Surface (m²)</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.toiture?.surface ? 'input-error' : ''}`}
                        value={formData.toiture.surface}
                        onChange={(e) => handleChange('toiture', 'surface', Number(e.target.value))}
                      />
                      {errors.toiture?.surface && (
                        <span className="error-message">{errors.toiture.surface}</span>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Type d'isolation</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.toiture.isolation?.type || ''}
                        onChange={(e) => handleChange('toiture', 'isolation', { type: e.target.value })}
                      >
                        <option value="">Aucune isolation</option>
                        <option value="laineVerre">Laine de verre</option>
                        <option value="laineRoche">Laine de roche</option>
                        <option value="polystyrene">Polystyrène</option>
                        <option value="polyurethane">Polyuréthane</option>
                        <option value="fibreBois">Fibre de bois</option>
                        <option value="chanvre">Chanvre</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 'plancher' && (
                <div className="form-container">
                  <h2 className="section-title">Caractéristiques du plancher</h2>
                  <div className="space-y-4">
                    <h2 className="text-xl font-bold mb-4">Plancher</h2>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="input-group">
                        <label className="input-label">Type de plancher</label>
                        <select
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.plancher.type}
                          onChange={(e) => handleChange('plancher', 'type', e.target.value)}
                        >
                          <option value="terrePlein">Terre-plein</option>
                          <option value="videSanitaire">Vide sanitaire</option>
                          <option value="cave">Cave</option>
                        </select>
                      </div>

                      <div className="input-group">
                        <label className="input-label">Matériau du plancher</label>
                        <select
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.plancher.materiaux}
                          onChange={(e) => handleChange('plancher', 'materiaux', e.target.value)}
                        >
                          <option value="beton">Béton</option>
                          <option value="bois">Bois</option>
                          <option value="terre">Terre</option>
                          <option value="pierre">Pierre</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Surface (m²)</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.plancher?.surface ? 'input-error' : ''}`}
                        value={formData.plancher.surface}
                        onChange={(e) => handleChange('plancher', 'surface', Number(e.target.value))}
                      />
                      {errors.plancher?.surface && (
                        <span className="error-message">{errors.plancher.surface}</span>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Type d'isolation</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.plancher.isolation?.type || ''}
                        onChange={(e) => handleChange('plancher', 'isolation', { type: e.target.value })}
                      >
                        <option value="">Aucune isolation</option>
                        <option value="laineVerre">Laine de verre</option>
                        <option value="laineRoche">Laine de roche</option>
                        <option value="polystyrene">Polystyrène</option>
                        <option value="polyurethane">Polyuréthane</option>
                        <option value="fibreBois">Fibre de bois</option>
                        <option value="chanvre">Chanvre</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 'fenetres' && (
                <div className="form-container">
                  <h2 className="section-title">Caractéristiques des fenêtres</h2>
                  <div className="space-y-4">
                    <div className="input-group">
                      <label className="input-label">Type de vitrage</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                     appearance-none cursor-pointer
                                     bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20fill%3D%22%236B7280%22%2F%3E%3C%2Fsvg%3E')] 
                                     bg-no-repeat bg-[length:1.5em_1.5em] bg-[right_0.5rem_center]"
                        value={formData.fenetres.type}
                        onChange={(e) => handleChange('fenetres', 'type', e.target.value)}
                      >
                        <option value="simple">Simple vitrage</option>
                        <option value="double">Double vitrage</option>
                        <option value="triple">Triple vitrage</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Surface totale (m²)</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.fenetres?.surface ? 'input-error' : ''}`}
                        value={formData.fenetres.surface}
                        onChange={(e) => handleChange('fenetres', 'surface', Number(e.target.value))}
                      />
                      {errors.fenetres?.surface && (
                        <span className="error-message">{errors.fenetres.surface}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 'chauffage' && (
                <div className="form-container">
                  <h2 className="section-title">Caractéristiques du chauffage</h2>
                  <div className="space-y-4">
                    <h2 className="text-xl font-bold mb-4">Système de chauffage</h2>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="input-group">
                        <label className="input-label">Type de chauffage</label>
                        <select
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.chauffage.type}
                          onChange={(e) => handleChange('chauffage', 'type', e.target.value)}
                        >
                          <option value="gazRecent">Chaudière gaz récente</option>
                          <option value="gazAncien">Chaudière gaz ancienne</option>
                          <option value="fioulRecent">Chaudière fioul récente</option>
                          <option value="fioulAncien">Chaudière fioul ancienne</option>
                          <option value="electrique">Chauffage électrique</option>
                          <option value="pac">Pompe à chaleur</option>
                        </select>
                      </div>

                      <div className="input-group">
                        <label className="input-label">Émetteurs de chaleur</label>
                        <select
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.chauffage.emetteurs}
                          onChange={(e) => handleChange('chauffage', 'emetteurs', e.target.value)}
                        >
                          <option value="radiateurs">Radiateurs</option>
                          <option value="plancherChauffant">Plancher chauffant</option>
                          <option value="poutresRayonnantes">Poutres rayonnantes</option>
                          <option value="radiateursElectriques">Radiateurs électriques</option>
                          <option value="poeleGranules">Poêle à granulés</option>
                          <option value="poeleBois">Poêle à bois</option>
                          <option value="climatisationReversible">Climatisation réversible</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-5 gap-4 items-end">
                      <div className="input-group">
                        <label className="input-label">Énergie utilisée</label>
                        <select
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.chauffage.energie}
                          onChange={(e) => handleChange('chauffage', 'energie', e.target.value)}
                        >
                          <option value="gaz">Gaz naturel</option>
                          <option value="fioul">Fioul domestique</option>
                          <option value="electricite">Électricité</option>
                          <option value="bois">Bois bûche</option>
                          <option value="granules">Granulés de bois</option>
                          <option value="solaire">Énergie solaire</option>
                        </select>
                      </div>

                      <div className="input-group">
                        <label className="input-label">Rendement (%)</label>
                        <input
                          type="number"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.chauffage.rendement || ''}
                          onChange={(e) => handleChange('chauffage', 'rendement', Number(e.target.value))}
                          min="0"
                          max="100"
                          step="1"
                          placeholder="Optionnel"
                        />
                      </div>

                      <div className="input-group">
                        <label className="input-label">Année d'installation</label>
                        <input
                          type="number"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.chauffage.dateInstallation}
                          onChange={(e) => handleChange('chauffage', 'dateInstallation', Number(e.target.value))}
                          min="1950"
                          max={new Date().getFullYear()}
                          step="1"
                        />
                      </div>

                      <div className="input-group flex items-center">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            checked={formData.chauffage.hasVeilleuse || false}
                            onChange={(e) => handleChange('chauffage', 'hasVeilleuse', e.target.checked)}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Veilleuse permanente</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 'ecs' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-bold mb-4">Eau Chaude Sanitaire</h2>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="input-group">
                      <label className="input-label">Type de système</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.ecs.type}
                        onChange={(e) => handleChange('ecs', 'type', e.target.value)}
                      >
                        <option value="chaudiereDouble">Chaudière double service</option>
                        <option value="ballonElectrique">Ballon électrique</option>
                        <option value="ballonThermodynamique">Ballon thermodynamique</option>
                        <option value="chauffeEauGaz">Chauffe-eau gaz</option>
                        <option value="chauffeEauSolaire">Chauffe-eau solaire</option>
                        <option value="chauffeEauThermodynamique">Chauffe-eau thermodynamique</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Énergie utilisée</label>
                      <select
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.ecs.energie}
                        onChange={(e) => handleChange('ecs', 'energie', e.target.value)}
                      >
                        <option value="electricite">Électricité</option>
                        <option value="gaz">Gaz naturel</option>
                        <option value="fioul">Fioul</option>
                        <option value="solaire">Solaire</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="input-group">
                      <label className="input-label">Puissance (kW)</label>
                      <input
                        type="number"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.ecs.puissance || ''}
                        onChange={(e) => handleChange('ecs', 'puissance', Number(e.target.value))}
                        min="0"
                        max="50"
                        step="0.1"
                        placeholder="Optionnel"
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label">Rendement (%)</label>
                      <input
                        type="number"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.ecs.rendement || ''}
                        onChange={(e) => handleChange('ecs', 'rendement', Number(e.target.value))}
                        min="0"
                        max="100"
                        step="1"
                        placeholder="Optionnel"
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label">Année de fabrication</label>
                      <input
                        type="number"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.ecs.dateFabrication || ''}
                        onChange={(e) => handleChange('ecs', 'dateFabrication', Number(e.target.value))}
                        min="1950"
                        max={new Date().getFullYear()}
                        step="1"
                        placeholder="Optionnel"
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 'temperatures' && (
                <div className="form-container">
                  <h2 className="section-title">Températures</h2>
                  <div className="space-y-4">
                    <div className="input-group">
                      <label className="input-label">Température intérieure (°C)</label>
                      <input 
                        type="number"
                        className={`number-input ${errors.temperatures?.interieure ? 'input-error' : ''}`}
                        value={formData.temperatures.interieure}
                        onChange={(e) => handleChange('temperatures', 'interieure', Number(e.target.value))}
                        min="16"
                        max="25"
                        step="0.5"
                      />
                      {errors.temperatures?.interieure && (
                        <span className="error-message">{errors.temperatures.interieure}</span>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Température extérieure moyenne (°C)</label>
                      <input 
                        type="number"
                        className="number-input"
                        value={formData.temperatures.exterieure}
                        onChange={(e) => handleChange('temperatures', 'exterieure', Number(e.target.value))}
                        step="0.5"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="button-group">
                <button 
                  onClick={prevStep}
                  className="button button-secondary"
                  disabled={currentStep === 'general'}
                >
                  Précédent
                </button>
                <button 
                  onClick={nextStep}
                  className="button button-primary"
                >
                  {currentStep === 'temperatures' ? 'Calculer' : 'Suivant'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex gap-8">
            {/* Sommaire et bouton */}
            <div className="sticky top-8 h-fit w-64 shrink-0 space-y-4">
              {/* Sommaire */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {[
                    { id: 'recap', label: 'Récapitulatif des données saisies' },
                    { id: 'bilan', label: 'Bilan énergétique actuel' },
                    { id: 'conso', label: 'Consommation énergétique' },
                    { id: 'impact', label: 'Impact du système de chauffage' },
                    { id: 'reco', label: 'Recommandations de travaux' },
                    ...(getActiveRecommandationsTotals.economiesAnnuelles > 0 ? [
                      { id: 'resume-financier', label: 'Résumé financier des travaux' },
                      { id: 'visualisation', label: 'Visualisation des coûts' }
                    ] : [])
                  ].map(({ id, label }) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="block text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 
                                 dark:hover:text-blue-400 transition-colors duration-200"
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>

              {/* Bouton nouvelle simulation */}
              <button 
                onClick={() => {
                  setResults(null);
                  setCurrentStep('general');
                }}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                           transition-colors duration-200 shadow-md"
              >
                Nouvelle simulation
              </button>
            </div>

            {/* Contenu principal */}
            <div className="flex-1 min-w-0 space-y-8">
              {/* Récapitulatif des données */}
              <div className="result-card bg-white dark:bg-gray-800 p-6" id="recap">
                <div 
                  onClick={() => setShowRecap(!showRecap)}
                  className="flex justify-between items-center cursor-pointer"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Récapitulatif des données saisies
                  </h3>
                  <svg 
                    className={`w-6 h-6 transform transition-transform ${showRecap ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                <div className={`mt-4 space-y-4 overflow-hidden transition-all duration-200 ${
                  showRecap ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300">Informations générales</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Type de logement</span>
                          <span>{formData.general.typeLogement}</span>
                        </div>
                        {formData.general.typeLogement === 'immeuble' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400 font-bold">Usage principal</span>
                            <span>{formData.general.usagePrincipalImmeuble}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Surface habitable</span>
                          <span>{formData.general.surfaceHabitable} m²</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Nombre d'étages</span>
                          <span>{formData.general.nombreEtages}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Année de construction</span>
                          <span>{formData.general.anneeConstruction}</span>
                        </div>
                        {(formData.general.typeLogement === 'appartement' || formData.general.typeLogement === 'immeuble') && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400 font-bold">En copropriété</span>
                            <span>{formData.general.copropriete ? 'Oui' : 'Non'}</span>
                          </div>
                        )}
                        {formData.general.copropriete && formData.general.numeroImmatriculationCopropriete && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400 font-bold">N° d'immatriculation</span>
                            <span>{formData.general.numeroImmatriculationCopropriete}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Zone climatique</span>
                          <span>{formData.general.zoneClimatique}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Température intérieure</span>
                          <span>{formData.temperatures.interieure}°C</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Température extérieure</span>
                          <span>{formData.temperatures.exterieure}°C</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300">Adresse du logement</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Rue</span>
                          <span>{formData.general.adresse.rue}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Code postal</span>
                          <span>{formData.general.adresse.codePostal}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400 font-bold">Ville</span>
                          <span>{formData.general.adresse.ville}</span>
                        </div>
                        {formData.general.adresse.complementAdresse && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400 font-bold">Complément</span>
                            <span>{formData.general.adresse.complementAdresse}</span>
                          </div>
                        )}
                        {formData.general.adresse.referenceCadastrale && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400 font-bold">Référence cadastrale</span>
                            <span>{formData.general.adresse.referenceCadastrale}</span>
                          </div>
                        )}
                        {(formData.general.typeLogement === 'appartement' || formData.general.typeLogement === 'immeuble') && (
                          <>
                            {formData.general.adresse.etage && (
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400 font-bold">Étage</span>
                                <span>{formData.general.adresse.etage}</span>
                              </div>
                            )}
                            {formData.general.adresse.numeroAppartement && (
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400 font-bold">N° d'appartement</span>
                                <span>{formData.general.adresse.numeroAppartement}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">État de l'isolation</h4>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400 font-bold">Murs</span>
                        <span>{formData.murs.isolation?.type || 'Non isolé'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400 font-bold">Toiture</span>
                        <span>{formData.toiture.isolation?.type || 'Non isolé'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400 font-bold">Plancher</span>
                        <span>{formData.plancher.isolation?.type || 'Non isolé'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Résumé global */}
              <div className="stats-card bg-white dark:bg-gray-800 p-6" id="bilan">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Bilan énergétique actuel
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Déperditions totales</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {formatNumber(results.deperditions.total)} W/K
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Consommation annuelle</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {formatNumber(results.consommation)} kWh/an
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Consommation par m²</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {formatNumber(results.consommation / formData.general.surfaceHabitable)} kWh/m²/an
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Coût annuel estimé</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {formatNumber(results.consommation * 0.174)} €/an
                      </span>
                    </div>
                  </div>
                </div>

                {/* Répartition des déperditions */}
                <div className="mt-6">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    Répartition des déperditions
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(results.deperditions.parComposant).map(([composant, valeur]) => {
                      const pourcentage = (valeur / results.deperditions.total) * 100;
                      return (
                        <div key={composant} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-300">
                              {composant.charAt(0).toUpperCase() + composant.slice(1) + ' (' + Math.round(valeur) + ' W/K)'}
                            </span>
                            <span className="text-gray-900 dark:text-white">
                              {pourcentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full"
                              style={{ width: `${pourcentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Consommation */}
              <div className="stats-card bg-white p-6" id="conso">
                <h3 className="text-xl font-semibold mb-4 text-green-800">
                  Consommation énergétique
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <span>Annuelle</span>
                    <span className="font-bold text-lg">
                      {results.consommation.toFixed(0)} kWh/an
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Par m²</span>
                      <span>
                        {(results.consommation / formData.general.surfaceHabitable).toFixed(0)} kWh/m²/an
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Température de consigne</span>
                      <span>{formData.temperatures.interieure}°C</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Température extérieure moyenne</span>
                      <span>{formData.temperatures.exterieure}°C</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact du chauffage actuel */}
              <div className="stats-card bg-white dark:bg-gray-800 p-6" id="impact">
                <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-300 mb-4">
                  Impact du système de chauffage actuel
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2">
                    <span className="text-gray-600 dark:text-gray-300">Type de chauffage</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatChauffageType(formData.chauffage.type)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Rendement estimé</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {(() => {
                        const rendements = {
                          'gazRecent': '95%',
                          'gazAncien': '65%',
                          'electrique': '100%',
                          'pac': '300%',
                          'fioulRecent': '92%',
                          'fioulAncien': '60%',
                          'granules': '85%',
                          'solaire': '400%'
                        };
                        return rendements[formData.chauffage.type as keyof typeof rendements] || '80%';
                      })()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Impact environnemental</span>
                    <span className={`font-medium ${
                      ['gazAncien', 'fioulAncien', 'fioulRecent'].includes(formData.chauffage.type) 
                        ? 'text-red-600 dark:text-red-400'
                        : ['pac', 'solaire'].includes(formData.chauffage.type)
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-orange-600 dark:text-orange-400'
                    }`}>
                      {(() => {
                        if (['gazAncien', 'fioulAncien', 'fioulRecent'].includes(formData.chauffage.type)) {
                          return 'Élevé';
                        } else if (['pac', 'solaire'].includes(formData.chauffage.type)) {
                          return 'Faible';
                        }
                        return 'Modéré';
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recommandations */}
              <div className="result-card bg-white p-6" id="reco">
                <h3 className="text-xl font-semibold mb-6 text-orange-800">
                  Recommandations de travaux
                </h3>
                <div className="space-y-6">
                  {results.recommandations.length > 0 ? results.recommandations.map((reco, index) => (
                    <div 
                      key={index}
                      onClick={() => toggleRecommandation(index)}
                      className={`border-l-4 ${
                        reco.priorite === 1 ? 'border-red-500' : 'border-orange-300'
                      } p-4 bg-gray-50 rounded-r-lg cursor-pointer transition-all duration-200 ${
                        activeRecommandations[index] === true 
                          ? 'bg-blue-50 transform -translate-x-2' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-lg mb-2">{reco.travaux}</h4>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>Économie annuelle : {reco.economieEnergie.toFixed(0)} kWh/an</p>
                            <p>Économie financière : {(reco.economieEnergie * 0.174).toFixed(0)} €/an</p>
                            {reco.composant === 'chauffage' && (
                              <p>Puissance recommandée : {estimerPuissanceChauffage(results.deperditions.total).toFixed(1)} kW</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{reco.coutEstime.toFixed(0)} €</p>
                          <p className="text-sm text-gray-600">
                            Temps d'amortissement : {reco.tempsRetour.toFixed(1)} ans
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-end mt-2">
                        <span className={`text-sm ${
                          activeRecommandations[index] === true 
                            ? 'text-blue-600' 
                            : 'text-gray-500'
                        }`}>
                          {activeRecommandations[index] === true ? 'Sélectionné' : 'Cliquer pour sélectionner'}
                        </span>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-600 dark:text-gray-300">
                      Aucune recommandation de travaux n'est disponible.
                    </p>
                  )}
                </div>
              </div>

              {/* Section résumé financier */}
              {getActiveRecommandationsTotals.economiesAnnuelles > 0 && (
                <div className="result-card bg-white dark:bg-gray-800 p-6" id="resume-financier">
                  <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-300 mb-4">
                    Résumé financier des travaux sélectionnés
                  </h3>
                  
                  <h4 className="text-lg font-medium mb-3 text-gray-900 dark:text-gray-100">
                    Données financières
                  </h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-300">Investissement total</span>
                        <span className="font-bold text-xl text-gray-900 dark:text-white">
                          {formatNumber(getActiveRecommandationsTotals.coutTotal)} €
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-300">Économies annuelles</span>
                        <span className="font-bold text-green-600 dark:text-green-400">
                          {formatNumber(getActiveRecommandationsTotals.economiesAnnuelles)} €/an
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-300">Temps d'amortissement</span>
                        <span className={`font-bold ${
                          getAmortissementClass(getActiveRecommandationsTotals.coutTotal / getActiveRecommandationsTotals.economiesAnnuelles)
                        }`}>
                          {formatNumber(getActiveRecommandationsTotals.coutTotal / getActiveRecommandationsTotals.economiesAnnuelles)} ans
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-300">Gain sur 20 ans</span>
                        <span className="font-bold text-green-600 dark:text-green-400">
                          {formatNumber(getActiveRecommandationsTotals.economiesAnnuelles * 20 - getActiveRecommandationsTotals.coutTotal)} €
                        </span>
                      </div>
                    </div>
                  </div>

                  <h4 className="text-lg font-medium mt-3 mb-3 text-gray-900 dark:text-gray-100">
                    Analyse financière
                  </h4>
                  {(() => {
                    const totals = getActiveRecommandationsTotals;
                    const advice = getFinancialAdvice(totals.coutTotal, totals.economiesAnnuelles);
                    
                    return (
                      <div className="space-y-3">
                        <p className={`${advice.color} font-medium`}>
                          {advice.type === 'excellent' && '🌟 '}
                          {advice.type === 'bon' && '👍 '}
                          {advice.type === 'moyen' && '⚖️ '}
                          {advice.type === 'long' && '⏳ '}
                          {advice.message}
                        </p>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          <ul className="list-disc list-inside space-y-1">
                            <li>
                              Investissement initial : {formatNumber(totals.coutTotal)}€
                            </li>
                            <li>
                              Économies annuelles estimées : {formatNumber(totals.economiesAnnuelles)}€
                            </li>
                            <li>
                              Gain total sur 20 ans : {formatNumber(totals.economiesAnnuelles * 20 - totals.coutTotal)}€
                            </li>
                          </ul>
                        </div>
                        
                        {totals.coutTotal > 15000 && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 italic">
                            💡 Des aides financières peuvent être disponibles pour ces travaux. Renseignez-vous auprès de votre conseiller France Rénov'.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Section visualisation des coûts */}
              {getActiveRecommandationsTotals.economiesAnnuelles > 0 && (
                <div className="result-card bg-white dark:bg-gray-800 p-6" id="visualisation">
                  <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-300 mb-6">
                    Visualisation des coûts sur 20 ans
                  </h3>

                  <div className="h-[400px] mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={costData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 35 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="annee" 
                          label={{ value: 'Années', position: 'bottom', offset: 15 }}
                        />
                        <YAxis 
                          label={{ value: 'Coûts cumulés (€)', angle: -90, position: 'left' }}
                          tickFormatter={(value) => `${(value/1000).toFixed(0)}k€`}
                        />
                        <Tooltip 
                          formatter={(value) => [`${(Number(value)/1000).toFixed(1)}k€`]}
                          labelFormatter={(label) => `Année ${label}`}
                        />
                        <Legend 
                          verticalAlign="top"
                          height={36}
                        />

                        {getActiveRecommandationsTotals.economiesAnnuelles > 0 && (
                          <ReferenceLine
                            x={Math.round(getActiveRecommandationsTotals.coutTotal / getActiveRecommandationsTotals.economiesAnnuelles)}
                            stroke="#16a34a"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            isFront={true}
                            label={{
                              value: `Seuil d'amortissement : ${(getActiveRecommandationsTotals.coutTotal / getActiveRecommandationsTotals.economiesAnnuelles).toFixed(1)} ans`,
                              position: 'insideTopRight',
                              fill: '#16a34a',
                              fontSize: 12,
                              offset: 10
                            }}
                          />
                        )}

                        <Line 
                          type="monotone" 
                          dataKey="sansTravaux" 
                          name="Sans travaux" 
                          stroke="#ef4444" 
                          strokeWidth={2}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="avecTravaux" 
                          name="Avec travaux" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
